import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { SENSORS } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, Cpu, Copy, Cable, Library, Pencil } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

function LastSeen({ ts }) {
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!ts) return <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><span className="w-2 h-2 rounded-full bg-muted-foreground" /> belum ada</span>;
  const age = (now - new Date(ts).getTime()) / 1000;
  let color = "bg-primary";
  if (age >= 30) color = "bg-destructive";
  else if (age >= 15) color = "bg-accent";
  const label = age < 60 ? `${age.toFixed(0)}s lalu` : `${Math.floor(age / 60)}m lalu`;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-mono text-muted-foreground">
      <span className={`w-2 h-2 rounded-full ${color}`} />
      {label}
    </span>
  );
}

const TYPES = [
  { value: "soil_moisture", label: "Soil Moisture", unit: "%" },
  { value: "flow_in", label: "Flowmeter IN", unit: "L/min" },
  { value: "flow_out", label: "Flowmeter OUT", unit: "L/min" },
  { value: "pzem", label: "PZEM (Solar SoC)", unit: "%" },
  { value: "solenoid", label: "Solenoid Valve", unit: "on/off" },
  // Extended catalog
  { value: "wind_speed", label: "Wind Speed (Anemometer)", unit: "m/s" },
  { value: "dht22", label: "DHT22 (Temp + Humidity)", unit: "°C/%" },
  { value: "bmp280", label: "BMP280 (Pressure + Altitude)", unit: "hPa" },
  { value: "bh1750", label: "BH1750 (Light Intensity)", unit: "lux" },
  { value: "ds18b20", label: "DS18B20 (Temperature)", unit: "°C" },
  { value: "gps_neo6m", label: "GPS Neo-6M (Lat/Lng)", unit: "lat,lng" },
  { value: "as7262", label: "AS7262 (Spectral Sensor)", unit: "spectrum" },
  { value: "bpw34", label: "BPW34 (Solar Irradiance)", unit: "W/m²" },
  { value: "si1145", label: "SI1145 (UV Index)", unit: "UV index" },
  { value: "bme280", label: "BME280 (P+T+RH)", unit: "hPa/°C/%" },
  { value: "sht31", label: "SHT31 (Temp + Humidity)", unit: "°C/%" },
  { value: "npk", label: "NPK (Nutrient Ion Selective)", unit: "ppm" },
  { value: "ph_soil", label: "pH Tanah", unit: "pH" },
  { value: "as7265x", label: "AS7265x (Triad Spectroscopy 18-ch)", unit: "spectrum" },
  { value: "pyranometer", label: "Pyranometer (Solar Radiation)", unit: "W/m²" },
  { value: "generic", label: "Generic (Custom Arduino-compatible)", unit: "" },
];

const DATA_TYPES = [
  { value: "analog", label: "Analog (ADC)" },
  { value: "digital", label: "Digital (GPIO)" },
  { value: "i2c", label: "I²C (SDA/SCL)" },
  { value: "spi", label: "SPI" },
  { value: "uart", label: "UART/Serial" },
];

export default function Sensors() {
  const { currentId, current } = useProject();
  const { user } = useAuth();
  // "Admin Anggota" (project_admin) juga boleh menambah/edit/hapus sensor pada project mereka.
  // Backend enforcement tetap di assert_project_admin (require_project_admin), jadi ini hanya UI gate.
  const canEdit = user?.role === "admin" || user?.role === "project_admin";

  const [sensors, setSensors] = useState([]);
  const [catalog, setCatalog] = useState({}); // per-type metadata from /sensor-catalog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "soil_moisture", pin: "", data_type: "" });

  // Edit dialog state — separate from Add so the two flows don't collide.
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null); // {id, name, type, pin, i2c_address, data_type}
  const editTypeMeta = editForm ? (catalog[editForm.type] || { i2c_address: null, library_deps: [], needs_pin: true }) : null;

  // Per-selected-type metadata (drives form behaviour)
  const typeMeta = catalog[form.type] || { i2c_address: null, library_deps: [], needs_pin: true };

  const fetch_ = async () => {
    if (!currentId) return;
    const { data } = await api.get(`/projects/${currentId}/sensors`);
    setSensors(data);
  };

  useEffect(() => {
    // Load the public sensor catalog once — drives form hints & table badges.
    api.get(`/sensor-catalog`).then(({ data }) => setCatalog(data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetch_();
    const id = setInterval(fetch_, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  // When user switches to an I²C-only sensor, blank out the pin so we don't carry over a stale value.
  useEffect(() => {
    if (!typeMeta.needs_pin && form.pin) {
      setForm((p) => ({ ...p, pin: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.type, typeMeta.needs_pin]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, project_id: currentId };
      if (!payload.data_type) delete payload.data_type;
      await api.post("/sensors", payload);
      toast.success("Sensor berhasil ditambahkan");
      setDialogOpen(false);
      setForm({ name: "", type: "soil_moisture", pin: "", data_type: "" });
      fetch_();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (Array.isArray(detail) ? "Sensor tidak kompatibel — " + (detail[0]?.msg || "") : "Gagal menambah sensor");
      toast.error(msg);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus sensor ini?")) return;
    await api.delete(`/sensors/${id}`);
    toast.success("Sensor dihapus");
    fetch_();
  };

  const openEdit = (s) => {
    setEditForm({
      id: s.id,
      name: s.name || "",
      type: s.type,
      pin: s.pin || "",
      i2c_address: s.i2c_address || "",
      data_type: s.data_type || "",
    });
    setEditOpen(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editForm) return;
    const payload = {
      name: editForm.name,
      type: editForm.type,
      // Send only the relevant interface field for the current type.
      ...(editTypeMeta?.needs_pin
        ? { pin: editForm.pin || "", i2c_address: "" }
        : { i2c_address: editForm.i2c_address || "", pin: "" }),
      data_type: editForm.data_type || null,
    };
    try {
      await api.patch(`/sensors/${editForm.id}`, payload);
      toast.success("Sensor diperbarui");
      setEditOpen(false);
      setEditForm(null);
      fetch_();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Gagal memperbarui sensor");
    }
  };

  if (!currentId) return <div className="text-muted-foreground">Pilih proyek terlebih dulu.</div>;

  return (
    <div>
      <PageHeader
        title="Manajemen Sensor"
        overline={current?.name}
        description="Daftar sensor terhubung. Admin dapat menambah/menghapus sensor & mengonfigurasi pin ESP32."
        actions={
          canEdit && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid={SENSORS.addButton}>
                  <Plus className="w-4 h-4 mr-1" /> Tambah Sensor
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Tambah Sensor</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nama</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                      data-testid={SENSORS.nameInput}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipe sensor</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger data-testid={SENSORS.typeSelect}><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tipe data <span className="text-xs text-muted-foreground">(opsional)</span></Label>
                    <Select value={form.data_type || ""} onValueChange={(v) => setForm({ ...form, data_type: v })}>
                      <SelectTrigger><SelectValue placeholder="— auto-detect —" /></SelectTrigger>
                      <SelectContent>
                        {DATA_TYPES.map((d) => (
                          <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {typeMeta.needs_pin ? (
                    <div className="space-y-2">
                      <Label>Pin ESP32 (opsional)</Label>
                      <Input
                        value={form.pin}
                        onChange={(e) => setForm({ ...form, pin: e.target.value })}
                        placeholder="contoh GPIO34"
                        data-testid={SENSORS.pinInput}
                      />
                    </div>
                  ) : (
                    <div
                      className="border border-primary/30 bg-primary/5 rounded-md p-3 text-xs space-y-1"
                      data-testid="sensor-form-i2c-hint"
                    >
                      <div className="flex items-center gap-2 text-primary font-medium">
                        <Cable className="w-3.5 h-3.5" />
                        Sensor I²C — tidak perlu pin GPIO
                      </div>
                      <div className="text-muted-foreground">
                        Sambungkan ke pin SDA &amp; SCL ESP32 (default GPIO21 &amp; GPIO22).
                        Alamat I²C: <code className="font-mono text-foreground">{typeMeta.i2c_address || "—"}</code>
                      </div>
                    </div>
                  )}
                  {typeMeta.library_deps && typeMeta.library_deps.length > 0 && (
                    <div
                      className="border border-border rounded-md p-3 text-xs space-y-1.5"
                      data-testid="sensor-form-libs"
                    >
                      <div className="flex items-center gap-1.5 text-muted-foreground font-medium">
                        <Library className="w-3.5 h-3.5" /> Library Arduino:
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {typeMeta.library_deps.map((l) => (
                          <Badge key={l} variant="outline" className="text-[10px] font-mono">
                            {l}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  <DialogFooter>
                    <Button type="submit" data-testid={SENSORS.submitButton}>Simpan</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )
        }
      />

      <div className="border border-border rounded-lg bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nama</TableHead>
              <TableHead>Tipe</TableHead>
              <TableHead>Antarmuka</TableHead>
              <TableHead>Library</TableHead>
              <TableHead>Last seen</TableHead>
              <TableHead>Sensor ID</TableHead>
              {canEdit && <TableHead className="w-12"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sensors.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  Belum ada sensor.
                </TableCell>
              </TableRow>
            )}
            {sensors.map((s) => {
              const meta = catalog[s.type] || {};
              const i2c = s.i2c_address || meta.i2c_address;
              const libs = (Array.isArray(s.library_deps) && s.library_deps.length > 0)
                ? s.library_deps
                : (meta.library_deps || []);
              return (
              <TableRow key={s.id} data-testid={`sensor-row-${s.id}`}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <Cpu className="w-4 h-4 text-muted-foreground" />
                    {s.name}
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground capitalize">{s.type.replace("_", " ")}</TableCell>
                <TableCell>
                  {i2c ? (
                    <Badge
                      variant="outline"
                      className="border-primary/40 text-primary font-mono text-[10px]"
                      data-testid={`sensor-i2c-${s.id}`}
                    >
                      <Cable className="w-3 h-3 mr-1" /> I²C {i2c}
                    </Badge>
                  ) : s.pin ? (
                    <code className="font-mono text-xs">{s.pin}</code>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {libs.length === 0 ? (
                    <span className="text-xs text-muted-foreground">—</span>
                  ) : (
                    <div className="flex flex-wrap gap-1" data-testid={`sensor-libs-${s.id}`}>
                      {libs.slice(0, 2).map((l) => (
                        <Badge key={l} variant="outline" className="text-[10px] font-mono">
                          {l.replace(" Library", "").replace("Adafruit ", "")}
                        </Badge>
                      ))}
                      {libs.length > 2 && (
                        <Badge variant="outline" className="text-[10px]" title={libs.slice(2).join(", ")}>
                          +{libs.length - 2}
                        </Badge>
                      )}
                    </div>
                  )}
                </TableCell>
                <TableCell>{s.type === "solenoid" ? <span className="text-xs text-muted-foreground">—</span> : <LastSeen ts={s.last_reading_ts} />}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <code className="font-mono text-xs text-muted-foreground">{s.id.slice(0, 8)}…</code>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={async () => {
                        try { await navigator.clipboard.writeText(s.id); toast.success("Sensor ID disalin"); } catch { toast.error("Gagal menyalin"); }
                      }}
                      data-testid={`sensor-copy-${s.id}`}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </TableCell>
                {canEdit && (
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(s)}
                        data-testid={`sensor-edit-${s.id}`}
                        title="Edit sensor"
                      >
                        <Pencil className="w-4 h-4 text-primary" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(s.id)} data-testid={`sensor-delete-${s.id}`}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Edit dialog — reused for all rows via `editForm` state */}
      <Dialog open={editOpen} onOpenChange={(v) => { setEditOpen(v); if (!v) setEditForm(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Sensor</DialogTitle>
          </DialogHeader>
          {editForm && (
            <form onSubmit={submitEdit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nama</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  required
                  data-testid={SENSORS.editNameInput}
                />
              </div>
              <div className="space-y-2">
                <Label>Tipe sensor</Label>
                <Select value={editForm.type} onValueChange={(v) => setEditForm({ ...editForm, type: v })}>
                  <SelectTrigger data-testid={SENSORS.editTypeSelect}><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {editTypeMeta?.needs_pin ? (
                <div className="space-y-2">
                  <Label>Antarmuka — Pin ESP32</Label>
                  <Input
                    value={editForm.pin}
                    onChange={(e) => setEditForm({ ...editForm, pin: e.target.value })}
                    placeholder="contoh GPIO34"
                    data-testid={SENSORS.editPinInput}
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Antarmuka — Alamat I²C</Label>
                  <Input
                    value={editForm.i2c_address}
                    onChange={(e) => setEditForm({ ...editForm, i2c_address: e.target.value })}
                    placeholder={editTypeMeta?.i2c_address || "contoh 0x76"}
                    data-testid={SENSORS.editI2cInput}
                  />
                  <div className="text-xs text-muted-foreground">
                    Sensor I²C — sambungkan ke pin SDA &amp; SCL (default GPIO21 &amp; GPIO22).
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button type="submit" data-testid={SENSORS.editSubmitButton}>Simpan perubahan</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
